import toast from 'react-hot-toast';
import { takeEvery, put, call, select } from 'redux-saga/effects';
import * as campaignApi from '../../api/campaignApi';
import { push } from 'connected-react-router';
import * as actions from '../actions';
import * as selectors from '../selectors';
import moment from 'moment-timezone';

export function* getDigests() {
  try {
    yield put(actions.user.setLoading(true, 'Getting Daily Digests'));
    yield put(actions.campaign.clearCurrentDigestSuccess());
    const digests = yield call(campaignApi.getDigests);
    yield put(actions.campaign.getDigestsSuccess(digests));
  } catch (e) {
    yield put(actions.campaign.getDigestsFailure(e.message));
    toast.error('An error occurred retrieving digests.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* clearCurrentDigest() {
  try {
    yield put(actions.campaign.clearCurrentDigestSuccess());
  } catch (e) {
    yield put(actions.campaign.clearCurrentDigestFailure(e.message));
    toast.error('An error occurred.');
  }
}

export function* getDigestById({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Getting Daily Digest'));
    const digest = yield call(campaignApi.getDigestById, id);
    yield put(actions.campaign.getDigestByIdSuccess(digest));
  } catch (e) {
    yield put(actions.campaign.getDigestByIdFailure(e.message));
    toast.error('An error occurred retrieving the digest.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* addDailyDigest({ payload: { publication } }) {
  try {
    yield put(actions.campaign.clearCurrentDigest());
    yield put(actions.user.setLoading(true, 'Adding email'));

    let sendDate = moment().add(1, 'day');

    switch (publication) {
      case 'Hot Headlines':
        while (sendDate.day() !== 6) {
          sendDate.add(1, 'day');
        }
        break;
      case 'Dining Report':
        while (sendDate.day() !== 0) {
          sendDate.add(1, 'day');
        }
        break;
      default:
        sendDate = moment().add(1, 'day');
    }

    const newDigest = yield call(campaignApi.addDigest, {
      // subject: `${publication} [${sendDate.format('L')}]`,
      sendDate: sendDate.format('L'),
      style: publication,
    });

    yield put(actions.campaign.addDailyDigestSuccess());
    yield put(actions.campaign.getDigestByIdSuccess(newDigest));
    yield put(push(`/daily-digest/create/${newDigest.id}`));

    // toast.success('Email added');
  } catch (e) {
    yield put(actions.campaign.addDailyDigestFailure(e.message));
    toast.error('An error occurred creating the digest.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* updateDailyDigest({ payload: { digest } }) {
  try {
    yield put(actions.user.setLoading(true, 'Saving email'));
    yield call(campaignApi.updateDigest, digest);
    yield put(actions.campaign.updateDailyDigestSuccess());
    toast.success('Email updated');
  } catch (e) {
    yield put(actions.campaign.updateDailyDigestFailure(e.message));
    toast.error('An error occurred creating the digest.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* scheduleDailyDigest({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Scheduling email with MailChimp'));
    yield call(campaignApi.scheduleDigest, id);
    yield put(actions.campaign.scheduleDailyDigestSuccess());
  } catch (e) {
    yield put(actions.campaign.scheduleDailyDigestFailure(e.message));
    toast.error('An error occurred scheduling the email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

// HOTW
export function* getHotw() {
  try {
    yield put(actions.user.setLoading(true, 'Getting emails'));
    const publications = yield call(campaignApi.getHOTW);
    yield put(actions.campaign.getHotwSuccess(publications));
  } catch (e) {
    yield put(actions.campaign.getHotwFailure(e.message));
    toast.error('An error occurred retrieving emails.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* getHotwById({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Loading email'));
    const digest = yield call(campaignApi.getHotwById, id);
    yield put(actions.campaign.getHotwByIdSuccess(digest));
  } catch (e) {
    yield put(actions.campaign.getHotwByIdFailure(e.message));
    toast.error('An error occurred retrieving the publication.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

/**
 * @description Creates a new HOTW email
 * Do not include a send date by default
 */
export function* addHotw({ payload: { publication } }) {
  try {
    yield put(actions.user.setLoading(true, `Adding ${publication} email`));

    const { id } = yield call(campaignApi.addHOTW, {
      style: publication,
    });

    yield put(push(`/hotw/create/${id}`));
    yield put(actions.campaign.addHotwSuccess());
    // toast.success('Email created');
  } catch (e) {
    yield put(actions.campaign.addHotwFailure(e.message));
    toast.error('An error occurred creating the email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* cloneHotw({ payload: { publication } }) {
  try {
    const { mailchimpCampaignId, ...clone } = publication;
    yield put(actions.user.setLoading(true, 'Cloning email'));
    yield call(campaignApi.addHOTW, clone);
    yield put(actions.campaign.addHotwSuccess());
    yield put(push(`/hotw`));
    toast.success('email cloned');
  } catch (e) {
    yield put(actions.campaign.addHotwFailure(e.message));
    toast.error('An error occurred creating the email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* updateHotw({ payload: { publication } }) {
  try {
    yield put(actions.user.setLoading(true, 'Updating HOTW email'));
    if (!publication.sendDate) {
      publication.sendDate = null;
    }
    const update = yield call(campaignApi.updateHOTW, publication);
    yield put(actions.campaign.updateHotwSuccess(update));
    toast.success('Updated email');
  } catch (e) {
    yield put(actions.campaign.updateHotwFailure(e.message));
    toast.error('An error occurred creating the email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* scheduleHotw({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Scheduling email with MailChimp'));
    yield call(campaignApi.scheduleHOTW, id);
    yield put(actions.campaign.scheduleHotwSuccess());
  } catch (e) {
    yield put(actions.campaign.scheduleHotwFailure(e.message));
    toast.error('An error occurred scheduling the email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

// Promo Alerts
export function* getPromoAlerts() {
  try {
    yield put(actions.user.setLoading(true, 'Getting emails'));
    const publications = yield call(campaignApi.getPromoAlerts);
    yield put(actions.campaign.getPromoAlertsSuccess(publications));
  } catch (e) {
    yield put(actions.campaign.getPromoAlertsFailure(e.message));
    toast.error('An error occurred retrieving emails.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* getPromoAlertById({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Getting email'));
    const digest = yield call(campaignApi.getPromoAlertById, id);
    yield put(actions.campaign.getPromoAlertByIdSuccess(digest));
  } catch (e) {
    yield put(actions.campaign.getPromoAlertByIdFailure(e.message));
    toast.error('An error occurred retrieving the email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* addPromoAlert({ payload: { publication } }) {
  try {
    yield put(actions.user.setLoading(true, 'Adding Promo Alert'));

    const sendDate = moment().add(1, 'day');

    const { id } = yield call(campaignApi.addPromoAlert, {
      // subject: `${publication} [${sendDate.format('L')}]`,
      sendDate: sendDate.format('L'),
      style: publication,
    });

    const basePath = publication === 'City Rink' ? '/city-rink' : '/promo-alert';
    yield put(push(`${basePath}/create/${id}`));
    yield put(actions.campaign.addPromoAlertSuccess());
    // toast.success('Promo Alert added');
  } catch (e) {
    yield put(actions.campaign.addPromoAlertFailure(e.message));
    toast.error('An error occurred creating the digest.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* clonePromoAlert({ payload: { publication } }) {
  try {
    const { id, mailchimpCampaignId, ...update } = publication;
    yield put(actions.user.setLoading(true, 'Cloning Promo Alert'));
    const clone = yield call(campaignApi.addPromoAlert, update);
    yield put(actions.campaign.clonePromoAlertSuccess(clone));
    toast.success('Promo Alert cloned');
    const basePath = clone.style === 'City Rink' ? '/city-rink' : '/promo-alert';
    yield put(push(`${basePath}/create/${clone.id}`));
  } catch (e) {
    yield put(actions.campaign.clonePromoAlertFailure(e.message));
    toast.error('An error occurred creating the digest.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* updatePromoAlert({ payload: { publication } }) {
  try {
    yield put(actions.user.setLoading(true, 'Updating Promo Alert'));
    const update = yield call(campaignApi.updatePromoAlert, publication);
    yield put(actions.campaign.updatePromoAlertSuccess(update));
    toast.success('Promo Alert updated');
  } catch (e) {
    yield put(actions.campaign.updatePromoAlertFailure(e.message));
    toast.error('An error occurred creating the Promo Alert.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* schedulePromoAlert({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Scheduling Promo Alert'));
    yield call(campaignApi.schedulePromoAlert, id);
    yield put(actions.campaign.schedulePromoAlertSuccess());
  } catch (e) {
    yield put(actions.campaign.schedulePromoAlert(e.message));
    toast.error('An error occurred scheduling the Promo Alert.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* getTopPosts({ payload: { siteId, day, type } }) {
  try {
    console.log('get top posts');
    yield put(actions.user.setLoading(true, 'Getting posts from RebelMouse'));
    const posts = yield call(campaignApi.getTopPosts, { siteId, day, type });
    yield put(actions.campaign.getTopPostsSuccess(posts));
  } catch (e) {
    yield put(actions.campaign.getTopPostsFailure(e.message));
    toast.error('An error occurred retrieving the top posts.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* recentPosts({ payload: { siteId } }) {
  try {
    yield put(actions.user.setLoading(true, 'Getting posts from RebelMouse'));
    const posts = yield call(campaignApi.recentPosts, { siteId });
    yield put(actions.campaign.recentPostsSuccess(posts));
  } catch (e) {
    yield put(actions.campaign.recentPostsFailure(e.message));
    toast.error('An error occurred retrieving the recent posts.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* getHotwListingById({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true));
    const listing = yield call(campaignApi.getHotwListingById, id);
    yield put(actions.campaign.getHotwListingByIdSuccess(listing));
  } catch (e) {
    console.log(e);
    yield put(actions.campaign.getHotwListingByIdFailure(e.message));
    toast.error('An error occurred retrieving the listing.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* getDigestTestEmail({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Generating test email'));
    const listing = yield call(campaignApi.getDigestTestEmail, id);
    yield put(actions.campaign.getDigestTestEmailSuccess(listing));
  } catch (e) {
    console.log(e);
    yield put(actions.campaign.getDigestTestEmailFailure(e.message));
    toast.error('An error occurred while generating the test email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* getHotwTestEmail({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Generating test email'));
    const listing = yield call(campaignApi.getHotwTestEmail, id);
    yield put(actions.campaign.getHotwTestEmailSuccess(listing));
  } catch (e) {
    console.log(e);
    yield put(actions.campaign.getHotwTestEmailFailure(e.message));
    toast.error('An error occurred while generating the test email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* getPromoAlertTestEmail({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true, 'Generating test email'));
    const listing = yield call(campaignApi.getPromoAlertTestEmail, id);
    yield put(actions.campaign.getPromoAlertTestEmailSuccess(listing));
  } catch (e) {
    console.log(e);
    yield put(actions.campaign.getPromoAlertTestEmailFailure(e.message));
    toast.error('An error occurred while generating the test email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* deleteHotwById({ payload: { id } }) {
  try {
    const listing = yield call(campaignApi.deleteHotwById, id);
    const emails = yield select(selectors.campaigns.homesOfTheWeek);
    yield put(actions.campaign.getHotwSuccess(emails.filter(e => e.id !== id)));
    yield put(actions.campaign.deleteHotwByIdSuccess(listing));
    yield put(push('/hotw'));
  } catch (e) {
    console.log(e);
    yield put(actions.campaign.deleteHotwByIdFailure(e.message));
    toast.error('An error occurred while generating the test email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* deleteDigestById({ payload: { id } }) {
  try {
    yield call(campaignApi.deleteDigestById, id);
    const emails = yield select(selectors.campaigns.digests);
    const filtered = emails.filter(e => e.id !== id);
    yield put(actions.campaign.deleteDigestByIdSuccess(filtered));
    yield put(push('/daily-digest'));
  } catch (e) {
    console.log(e);
    yield put(actions.campaign.deleteDigestByIdFailure(e.message));
    toast.error('An error occurred while generating the test email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* deletePromoAlertById({ payload: { id, returnTo } }) {
  try {
    yield call(campaignApi.deletePromoAlertById, id);
    const emails = yield select(selectors.campaigns.promoAlerts);
    const filtered = emails.filter(e => e.id !== id);
    yield put(actions.campaign.deletePromoAlertByIdSuccess(filtered));
    yield put(push(returnTo || '/promo-alert'));
  } catch (e) {
    yield put(actions.campaign.deletePromoAlertByIdFailure(e.message));
    toast.error('An error occurred while generating the test email.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* searchDigests({ payload: { query } }) {
  try {
    yield put(actions.user.setLoading(true, 'Searching Daily Digest emails'));
    const digests = yield call(campaignApi.searchPosts, query);
    yield put(actions.campaign.searchDigestsSuccess(digests));
  } catch (e) {
    yield put(actions.campaign.searchDigestsFailure(e.message));
    toast.error('An error occurred search for posts.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* searchHOTW({ payload: { query } }) {
  try {
    yield put(actions.user.setLoading(true, 'Searching Home of the Week emails'));
    const digests = yield call(campaignApi.searchHOTW, query);
    yield put(actions.campaign.searchHOTWSuccess(digests));
  } catch (e) {
    yield put(actions.campaign.searchHOTWFailure(e.message));
    toast.error('An error occurred search for posts.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* searchPromoAlerts({ payload: { query } }) {
  try {
    yield put(actions.user.setLoading(true, 'Searching Home of the Week emails'));
    const digests = yield call(campaignApi.searchPromoAlerts, query);
    yield put(actions.campaign.searchPromoAlertsSuccess(digests));
  } catch (e) {
    yield put(actions.campaign.searchPromoAlertsFailure(e.message));
    toast.error('An error occurred search for posts.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* campaignSagas() {
  //digests
  yield takeEvery(actions.campaign.getDigests, getDigests);
  yield takeEvery(actions.campaign.getDigestById, getDigestById);
  yield takeEvery(actions.campaign.addDailyDigest, addDailyDigest);
  yield takeEvery(actions.campaign.updateDailyDigest, updateDailyDigest);
  yield takeEvery(actions.campaign.scheduleDailyDigest, scheduleDailyDigest);
  yield takeEvery(actions.campaign.getDigestTestEmail, getDigestTestEmail);
  yield takeEvery(actions.campaign.clearCurrentDigest, clearCurrentDigest);
  yield takeEvery(actions.campaign.deleteDigestById, deleteDigestById);

  //hotw
  yield takeEvery(actions.campaign.getHotw, getHotw);
  yield takeEvery(actions.campaign.deleteHotwById, deleteHotwById);
  yield takeEvery(actions.campaign.getHotwTestEmail, getHotwTestEmail);
  yield takeEvery(actions.campaign.getHotwById, getHotwById);
  yield takeEvery(actions.campaign.addHotw, addHotw);
  yield takeEvery(actions.campaign.cloneHotw, cloneHotw);
  yield takeEvery(actions.campaign.updateHotw, updateHotw);
  yield takeEvery(actions.campaign.scheduleHotw, scheduleHotw);
  yield takeEvery(actions.campaign.getHotwListingById, getHotwListingById);

  //promo
  yield takeEvery(actions.campaign.getPromoAlertTestEmail, getPromoAlertTestEmail);
  yield takeEvery(actions.campaign.getPromoAlerts, getPromoAlerts);
  yield takeEvery(actions.campaign.getPromoAlertById, getPromoAlertById);
  yield takeEvery(actions.campaign.addPromoAlert, addPromoAlert);
  yield takeEvery(actions.campaign.updatePromoAlert, updatePromoAlert);
  yield takeEvery(actions.campaign.clonePromoAlert, clonePromoAlert);
  yield takeEvery(actions.campaign.schedulePromoAlert, schedulePromoAlert);
  yield takeEvery(actions.campaign.deletePromoAlertById, deletePromoAlertById);

  //posts
  yield takeEvery(actions.campaign.getTopPosts, getTopPosts);
  yield takeEvery(actions.campaign.searchDigests, searchDigests);
  yield takeEvery(actions.campaign.searchHOTW, searchHOTW);
  yield takeEvery(actions.campaign.searchPromoAlerts, searchPromoAlerts);
  yield takeEvery(actions.campaign.recentPosts, recentPosts);
}
